import React from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import PartnerStrip from '../components/PartnerStrip';
import CtaBand from '../components/CtaBand';
import CountUp from '../components/CountUp';
import Marquee from '../components/Marquee';
import MartLogo from '../components/MartLogo';
import {
    capabilities,
    company,
    products,
    retailHighlights,
    services,
    stats,
    upcomingNote,
} from '../data/site';
import './pages.css';
import './Home.css';

/** Ticker contents: what we build, then what the stores sell. */
const marqueeItems = [
    ...products.map((product) => product.name),
    ...retailHighlights,
    'Cloud hosting',
    'WhatsApp automation',
    'Franchise partners welcome',
];

const Home: React.FC = () => (
    <>
        {/* ------------------------------------------------------------ Hero */}
        <section className="home-hero">
            <div className="glow home-hero__glow home-hero__glow--a glow--drift-a" />
            <div className="glow home-hero__glow home-hero__glow--b glow--drift-b" />

            <div className="container home-hero__inner">
                <div className="home-hero__copy">
                    <span className="chip chip--dark home-hero__badge enter" style={{ ['--i' as string]: 0 }}>
                        <span className="home-hero__pulse" aria-hidden="true" />
                        {company.legalName} · Est. {company.founded}
                    </span>

                    <h1 className="enter" style={{ ['--i' as string]: 1 }}>
                        Software that{' '}
                        <span className="grad-text grad-text--animated">digitalizes</span> schools,
                        businesses and service organizations.
                    </h1>

                    <p className="lede enter" style={{ ['--i' as string]: 2 }}>
                        Kifayti builds and operates a family of SaaS platforms — school ERP,
                        payroll, digital signage, quotations and repair management — backed by
                        in-house engineering, secure cloud infrastructure and our own payment
                        partners. We also run a growing SuperMart retail network.
                    </p>

                    <div className="btn-row home-hero__actions enter" style={{ ['--i' as string]: 3 }}>
                        <Link className="btn btn--primary btn--lg" to="/products">
                            Explore our products
                        </Link>
                        <Link className="btn btn--ghost-dark btn--lg" to="/services">
                            IT services
                        </Link>
                    </div>

                    <div className="chip-row home-hero__trust enter" style={{ ['--i' as string]: 4 }}>
                        <span className="chip chip--dark">Cashfree &amp; Razorpay partners</span>
                        <span className="chip chip--dark">GST {company.gst}</span>
                        <span className="chip chip--dark">CIN {company.cin}</span>
                    </div>
                </div>

                <aside
                    className="home-hero__panel"
                    aria-label="Kifayti platform ecosystem"
                    data-reveal="tilt"
                >
                    <div className="panel-head">
                        <span className="panel-dot" />
                        <span className="panel-dot" />
                        <span className="panel-dot" />
                        <span className="panel-title mono">kifayti / platforms</span>
                    </div>

                    <ul className="panel-list">
                        {products.map((product, i) => (
                            <li
                                key={product.slug}
                                style={{
                                    ['--accent' as string]: product.accent,
                                    ['--i' as string]: i,
                                }}
                            >
                                <span className="panel-list__dot" />
                                <span className="panel-list__name">{product.name}</span>
                                <span className="panel-list__cat">{product.category}</span>
                                <span className="panel-list__status">live</span>
                            </li>
                        ))}
                    </ul>

                    <div className="panel-foot">
                        <div>
                            <strong>Cloud</strong>
                            <span>Hosted &amp; backed up</span>
                        </div>
                        <div>
                            <strong>WhatsApp</strong>
                            <span>Automated alerts</span>
                        </div>
                        <div>
                            <strong>APIs</strong>
                            <span>Integration ready</span>
                        </div>
                    </div>
                </aside>
            </div>
        </section>

        {/* --------------------------------------------------------- Ticker */}
        <div className="mart-marquee">
            <Marquee items={marqueeItems} speed={38} />
        </div>

        <PartnerStrip />

        {/* -------------------------------------------------------- Products */}
        <section className="section section--dark home-products">
            <div className="container">
                <div className="sec-head sec-head--center" data-reveal="up">
                    <span className="kicker">Product suite</span>
                    <h2>
                        Five platforms, one{' '}
                        <span className="grad-text grad-text--animated">operating system</span> for
                        your organization
                    </h2>
                    <p className="lede">
                        Each product solves a real, repetitive operational problem — and they all
                        share the same cloud, authentication, payments and messaging backbone.
                    </p>
                </div>

                <div className="grid grid--3 home-products__grid">
                    {products.map((product, i) => (
                        <ProductCard key={product.slug} product={product} index={i} />
                    ))}

                    <div
                        className="product-upcoming"
                        data-reveal="up"
                        style={{ ['--i' as string]: products.length }}
                    >
                        <span className="product-upcoming__badge">In build</span>
                        <h3>{upcomingNote.title}</h3>
                        <p>{upcomingNote.description}</p>
                    </div>
                </div>

                <div className="home-products__cta" data-reveal="up">
                    <Link className="btn btn--ghost-dark" to="/products">
                        See full product details
                    </Link>
                </div>
            </div>
        </section>

        {/* ----------------------------------------------------------- Stats */}
        <section className="section section--tight section--light home-stats">
            <div className="container">
                <div className="grid grid--4">
                    {stats.map((stat, i) => (
                        <div
                            className="stat"
                            key={stat.label}
                            data-reveal="up"
                            style={{ ['--i' as string]: i }}
                        >
                            <strong className="grad-text">
                                <CountUp value={stat.value} />
                            </strong>
                            <span>{stat.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* -------------------------------------------------------- Services */}
        <section className="section section--alt">
            <div className="container">
                <div className="sec-head" data-reveal="up">
                    <span className="kicker">IT services</span>
                    <h2>Everything needed to take an organization digital</h2>
                    <p className="lede">
                        Beyond our own products, we plan, build, integrate and support the systems
                        organizations run on — from a school moving off paper registers to a
                        business that needs a custom platform of its own.
                    </p>
                </div>

                <div className="grid grid--3">
                    {services.map((service, i) => (
                        <article
                            className="card service-card"
                            key={service.title}
                            data-reveal="up"
                            style={{ ['--i' as string]: i % 3 }}
                        >
                            <span className="service-card__icon" aria-hidden="true">
                                {service.icon}
                            </span>
                            <h3>{service.title}</h3>
                            <p>{service.description}</p>
                            <ul>
                                {service.points.map((point) => (
                                    <li key={point}>{point}</li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>

                <div className="home-services__cta" data-reveal="up">
                    <Link className="btn btn--primary" to="/services">
                        Explore IT services
                    </Link>
                </div>
            </div>
        </section>

        {/* ---------------------------------------------------- Capabilities */}
        <section className="section section--light">
            <div className="container home-caps">
                <div className="home-caps__copy" data-reveal="left">
                    <span className="kicker">Why organizations choose us</span>
                    <h2>Built for daily use, not for demos</h2>
                    <p className="lede">
                        Our platforms are used by clerks, teachers, technicians and shop staff
                        every working day. That shapes every decision — speed on cheap hardware,
                        clarity over cleverness, and support that stays after go-live.
                    </p>
                    <Link className="link-arrow" to="/about">
                        More about how we work <span aria-hidden="true">→</span>
                    </Link>
                </div>

                <ul className="home-caps__list">
                    {capabilities.map((capability, i) => (
                        <li key={capability} data-reveal="right" style={{ ['--i' as string]: i }}>
                            <span className="tick" aria-hidden="true">
                                ✓
                            </span>
                            {capability}
                        </li>
                    ))}
                </ul>
            </div>
        </section>

        {/* ---------------------------------------------------------- Retail */}
        <section className="section section--alt home-retail">
            <div className="container home-retail__inner">
                <div className="home-retail__copy" data-reveal="left">
                    <span className="kicker">Retail division</span>
                    <h2>Kifayti SuperMart</h2>
                    <p className="lede">
                        Alongside the technology business, Kifayti operates SuperMart stores built
                        on a simple promise — quality products at honest prices, with everything a
                        family needs under one roof. Our stores run on the same technology we
                        build for clients: billing, inventory, payroll and in-store signage.
                    </p>

                    <ul className="home-retail__tags">
                        {retailHighlights.map((item, i) => (
                            <li key={item} data-reveal="up" style={{ ['--i' as string]: i }}>
                                {item}
                            </li>
                        ))}
                    </ul>

                    <div className="btn-row">
                        <Link className="btn btn--primary" to="/franchise">
                            Franchise opportunities
                        </Link>
                    </div>
                </div>

                <div className="home-retail__visual">
                    {/* The store brand, reversed out of the gradient exactly as
                        the mark is supplied. */}
                    <div className="mart-plate" data-reveal="zoom">
                        <MartLogo size="lg" />
                    </div>

                    <div className="home-retail__card" data-reveal="up" style={{ ['--i' as string]: 1 }}>
                        <span className="home-retail__badge">Franchise enquiries open</span>
                        <h3>Open a Kifayti mall in your city</h3>
                        <p>
                            We are expanding the SuperMart network through franchise partners.
                            Partners get a tested retail model, our in-house retail technology,
                            exclusive territory and hands-on setup support.
                        </p>
                        <Link className="link-arrow" to="/franchise">
                            See franchise details <span aria-hidden="true">→</span>
                        </Link>
                    </div>
                </div>
            </div>
        </section>

        <CtaBand
            title="Ready to digitalize your organization?"
            description="Tell us how you operate today and we will map out what moving to a Kifayti platform would actually look like — timelines, cost and rollout included."
        />
    </>
);

export default Home;
